/*!
 * camMotion.js — what a camera's frames say, as numbers: cam()'s signals
 * © 2026 Rob Canning — GPLv3
 *
 * Pure: a frame is its brightness, cell by cell, on a small grid (cam.js
 * draws the camera onto a 32 x 24 canvas and reads that); two frames give
 *
 *   motion   the share of the picture that changed, 0..1
 *   bright   how bright it is, on average, 0..1
 *   x, y     where the change is (its middle, 0..1 across and down), kept
 *            where it was last while nothing moves
 *
 * A cell counts as changed when its brightness moved by more than `step`:
 * a camera's own flicker (a level or two of 255, frame to frame) is not
 * movement, so a still room reads 0, not a jitter.
 *
 * And a hit: a burst of movement -- motion rising well above what it has
 * been lately (and above a floor), then not again until it has settled.
 */

/** RGBA bytes (a canvas's getImageData) to brightness 0..1 per cell. */
export function lumaOf(rgba, out = new Float32Array(rgba.length / 4)) {
  for (let i = 0, j = 0; j < out.length; i += 4, j++) {
    out[j] = (0.2126 * rgba[i] + 0.7152 * rgba[i + 1] + 0.0722 * rgba[i + 2]) / 255;
  }
  return out;
}

/**
 * Two frames' brightness (`prev` may be null: the first frame), `w` cells
 * across, read as if mirrored when `flip` (x as the picture shows it).
 * `last`: the x and y to keep while nothing moves.
 */
export function compareFrames(prev, cur, w, { step = 0.08, flip = false, last = { x: 0.5, y: 0.5 } } = {}) {
  const n = cur.length, h = Math.max(1, Math.round(n / w));
  let sum = 0;
  for (let i = 0; i < n; i++) sum += cur[i];
  const bright = n ? sum / n : 0;
  if (!prev || prev.length !== n) return { motion: 0, bright, x: last.x, y: last.y };
  let changed = 0, sx = 0, sy = 0;
  for (let i = 0; i < n; i++) {
    if (Math.abs(cur[i] - prev[i]) <= step) continue;
    changed++;
    sx += (i % w) + 0.5;
    sy += Math.floor(i / w) + 0.5;
  }
  if (!changed) return { motion: 0, bright, x: last.x, y: last.y };
  const x = sx / changed / w;
  return { motion: changed / n, bright, x: flip ? 1 - x : x, y: sy / changed / h };
}

/**
 * The hit detector's state, and one frame of it: true on a burst of
 * movement. Lately's average follows slowly; a hit is motion above
 * `ratio` times it and above `floor`, at least `gapMs` after the last one,
 * and only once motion has fallen back below half of what set it off.
 */
export const hitState = () => ({ avg: 0, armed: true, at: -Infinity, peak: 0 });

export function detectMotionHit(st, motion, now, { ratio = 2.5, floor = 0.03, gapMs = 150 } = {}) {
  const hit = st.armed && motion > floor && motion > st.avg * ratio && now - st.at >= gapMs;
  if (hit) { st.armed = false; st.at = now; st.peak = motion; }
  else if (!st.armed && motion < st.peak * 0.5) st.armed = true;
  st.avg = st.avg * 0.95 + motion * 0.05;
  return hit;
}
